import { USER_ROLES, USER_ROLE_LABELS, type UserRole } from '@dyeatlas/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Search, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Pagination } from './MasterDataTable'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Field, Input, Textarea } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import { Card, LoadingPanel, Notice } from '@/components/ui/misc'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { api, ApiError } from '@/lib/api'
import { cn, formatDate, formatRelative, initialsOf } from '@/lib/utils'

type AccountStatus = 'pending' | 'active' | 'rejected' | 'suspended'

interface AdminUser {
  id: string
  name: string
  email: string
  role: UserRole
  accountStatus: AccountStatus
  emailVerified: boolean
  createdAt: string
  lastLoginAt: string | null
  approvedAt: string | null
  institutionName: string | null
}

const STATUS_TONE = {
  pending: 'caution',
  active: 'positive',
  rejected: 'brand',
  suspended: 'neutral',
} as const

const PAGE_SIZE = 20

const ROLE_OPTIONS = USER_ROLES.map((role) => ({ value: role, label: USER_ROLE_LABELS[role] }))

export function UserManagement() {
  const queryClient = useQueryClient()
  const { user: currentUser } = useCurrentUser()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'all' | AccountStatus>('all')
  const [page, setPage] = useState(1)
  const [rejecting, setRejecting] = useState<AdminUser | null>(null)
  const [reason, setReason] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users', { search, status, page }],
    queryFn: () =>
      api.get<{ users: AdminUser[]; total: number; pendingCount: number }>(
        `/admin/users?${new URLSearchParams({
          ...(search ? { search } : {}),
          status,
          page: String(page),
          pageSize: String(PAGE_SIZE),
        })}`,
      ),
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })

  const setStatusMutation = useMutation({
    mutationFn: ({ id, nextStatus, note }: { id: string; nextStatus: AccountStatus; note?: string }) =>
      api.post(`/admin/users/${id}/status`, { status: nextStatus, reason: note }),
    onSuccess: async (_result, variables) => {
      await invalidate()
      setRejecting(null)
      setReason('')
      toast.success(
        variables.nextStatus === 'active'
          ? 'Account approved. The researcher has been emailed.'
          : variables.nextStatus === 'rejected'
            ? 'Registration rejected.'
            : 'Account updated.',
      )
    },
    onError: (failure) =>
      toast.error(failure instanceof ApiError ? failure.message : 'Could not update that account.'),
  })

  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: UserRole }) =>
      api.post(`/admin/users/${id}/role`, { role }),
    onSuccess: async () => {
      await invalidate()
      toast.success('Role updated.')
    },
    onError: (failure) =>
      toast.error(failure instanceof ApiError ? failure.message : 'Could not change that role.'),
  })

  const users = data?.users ?? []

  return (
    <>
      <header className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold text-ink">User management</h2>
          <p className="mt-0.5 text-[13px] text-ink-muted">
            Approve registrations and manage access. Super users approve publications and add
            references.
          </p>
        </div>
      </header>

      {Boolean(data?.pendingCount) && (
        <div className="mb-4">
          <Notice
            tone="caution"
            title={`${data?.pendingCount} registration${data?.pendingCount === 1 ? '' : 's'} awaiting review`}
            icon={<ShieldCheck className="size-4" />}
          >
            Researchers cannot sign in until an admin approves them. Approving sends a confirmation
            email.
          </Notice>
        </div>
      )}

      <div className="mb-4 flex items-center gap-3">
        <div className="flex-1">
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
            placeholder="Search name or email..."
            icon={<Search className="size-4" />}
          />
        </div>
        <Select
          value={status}
          onChange={(value) => {
            setStatus(value as typeof status)
            setPage(1)
          }}
          className="w-48"
          options={[
            { value: 'all', label: 'All accounts' },
            { value: 'pending', label: 'Pending approval' },
            { value: 'active', label: 'Active' },
            { value: 'rejected', label: 'Rejected' },
            { value: 'suspended', label: 'Suspended' },
          ]}
        />
      </div>

      <Card className="overflow-hidden">
        {isLoading ? (
          <LoadingPanel label="Loading accounts" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-paper-muted/60 text-left text-[12px] text-ink-muted">
                <th className="px-4 py-2.5 font-semibold">Researcher</th>
                <th className="px-4 py-2.5 font-semibold">Institution</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
                <th className="px-4 py-2.5 font-semibold">Role</th>
                <th className="px-4 py-2.5 font-semibold">Registered</th>
                <th className="px-4 py-2.5 font-semibold">Last logged on</th>
                <th className="px-4 py-2.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-b border-line last:border-0">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-soft text-[11px] font-semibold text-brand">
                        {initialsOf(user.name)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-ink">{user.name}</p>
                        <p className="truncate text-[12px] text-ink-muted">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-ink-muted">{user.institutionName ?? '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Badge tone={STATUS_TONE[user.accountStatus]}>{user.accountStatus}</Badge>
                      {!user.emailVerified && (
                        <span className="text-[11px] text-ink-faint">unverified</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Select
                      value={user.role}
                      onChange={(role) => setRole.mutate({ id: user.id, role: role as UserRole })}
                      disabled={user.id === currentUser?.id}
                      options={ROLE_OPTIONS}
                      className={cn(
                        'h-8 w-32 text-[12px]',
                        user.role !== 'researcher' && 'border-info/30 bg-info-soft text-info',
                      )}
                    />
                  </td>
                  <td className="px-4 py-3 text-ink-muted">{formatDate(user.createdAt)}</td>
                  <td className="px-4 py-3 text-ink-muted">
                    {user.lastLoginAt ? formatRelative(user.lastLoginAt) : 'Never'}
                  </td>
                  <td className="px-4 py-3">
                    {user.accountStatus === 'pending' ? (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() =>
                            setStatusMutation.mutate({ id: user.id, nextStatus: 'active' })
                          }
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setRejecting(user)
                            setReason('')
                          }}
                        >
                          Reject
                        </Button>
                      </div>
                    ) : user.accountStatus === 'active' ? (
                      <button
                        type="button"
                        onClick={() =>
                          setStatusMutation.mutate({ id: user.id, nextStatus: 'suspended' })
                        }
                        className="font-semibold text-brand hover:underline"
                      >
                        Suspend
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          setStatusMutation.mutate({ id: user.id, nextStatus: 'active' })
                        }
                        className="font-semibold text-positive hover:underline"
                      >
                        Reinstate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-ink-muted">
                    No accounts match those filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        <div className="flex items-center justify-between border-t border-line px-4 py-2.5 text-[12px] text-ink-muted">
          <Pagination
            page={page}
            totalPages={Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE))}
            onChange={setPage}
          />
          <span>{data?.total ?? 0} accounts</span>
        </div>
      </Card>

      <Dialog
        open={Boolean(rejecting)}
        onOpenChange={(open) => !open && setRejecting(null)}
        size="sm"
        title="Reject this registration?"
        description={rejecting ? `${rejecting.name} · ${rejecting.email}` : undefined}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRejecting(null)}>
              Cancel
            </Button>
            <Button
              loading={setStatusMutation.isPending}
              disabled={!reason.trim()}
              onClick={() =>
                rejecting &&
                setStatusMutation.mutate({
                  id: rejecting.id,
                  nextStatus: 'rejected',
                  note: reason.trim(),
                })
              }
            >
              Reject registration
            </Button>
          </>
        }
      >
        <Field
          label="Reason"
          htmlFor="reason"
          required
          hint="This is sent to the applicant, so keep it factual and actionable."
        >
          <Textarea
            id="reason"
            rows={4}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="We could not verify your affiliation with the institution given."
          />
        </Field>
      </Dialog>
    </>
  )
}
